import type * as ClassTypes from './types/class';
import {Navigation, Pagination} from 'swiper/modules';
import Counter from '@/components/counter';
import type {IDynamicImport} from './types/utils';
import type {IObserver} from '@/common/scripts/types/observer';
import type {IValidation} from '@/components/form/validation/types';
import Observer from './observer';
import popupContentTemplate from '@/components/form/form-parametric-content.twig';
import Swiper from 'swiper';
import {Utils} from './utils';
import Validation from '@/components/form/validation';

const validation: IValidation = new Validation();
const observer: IObserver = new Observer();

// Поведение шапки
export const initHeader = (headerWrap: HTMLElement): void => {
    import(/* webpackChunkName: "header" */ '@/components/header')
        .then(({default: Header}: IDynamicImport) => {
            const targetScroll: HTMLElement[] = [...document.querySelectorAll('.j-main-screen')] as HTMLElement[];
            const header: ClassTypes.IHeader = new Header();

            header.init({
                headerWrap,
                targetScroll
            });
        });
};

export const initLocation = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "location" */ '@/sections/location')
        .then(({default: Location}: IDynamicImport) => {
            const location = new Location(wrapper);

            location.init();
        });
};

/**
 * Инициализаяи слайдера с изображениями
 * @param {HTMLElement} sliderWrap - контейнер слайдера
 * @param {number} slidesLength - количество слайдов
 * @param {HTMLElement} paginationEl - контейнер счетчика
 */
const initImageSliderDefault = (sliderWrap: HTMLElement, slidesLength: number,
                                paginationEl: HTMLElement | null): void => {
    const swiper = new Swiper(sliderWrap, {
        modules      : [Navigation, Pagination],
        slidesPerView: 1,
        loop         : true,
        navigation   : {
            enabled: slidesLength > 1,
            nextEl : '.j-slider-arrow-next',
            prevEl : '.j-slider-arrow-prev'
        },
        pagination: {
            el  : paginationEl,
            type: 'custom',
            renderCustom(_: Swiper, current: number, total: number) {
                return `<span class="slider-control__current">${current.toString().padStart(2, '0')}</span>
                        <span class="slider-control__total">/ ${total.toString().padStart(2, '0')}</span>`;
            }
        },
        on: {
            afterInit: (element: Swiper) => {
                if (element.slides?.length === 1) {
                    element.el.classList.add('is-hide-arrows');
                }
            }
        }
    });

    swiper.init();
};

/**
 * Инициализация слайдера с последним слайдом попапом
 * @param {HTMLElement} sliderWrap - контейнер слайдера
 * @param {number} slidesLength - количество слайдов
 * @param {HTMLElement} paginationEl - контейнер буллетов
 * @param {string} sliderWithPopup - название элемента с попапом
 */
const initImageSliderPopup = (sliderWrap: HTMLElement, slidesLength: number,
                              paginationEl: HTMLElement | null, sliderWithPopup: string): void => {
    const swiper = new Swiper(sliderWrap, {
        modules      : [Navigation, Pagination],
        slidesPerView: 1,
        loop         : false,
        navigation   : {
            enabled: slidesLength > 1,
            nextEl : '.j-slider-arrow-next',
            prevEl : '.j-slider-arrow-prev'
        },
        pagination: {
            el               : paginationEl,
            type             : 'bullets',
            bulletActiveClass: 'is-active'
        },
        on: {
            afterInit: (element: Swiper) => {
                if (element.slides?.length === 1) {
                    element.el.classList.add('is-hide-arrows');
                }

                const bullet = document.createElement('span');

                bullet.classList.add('swiper-pagination-bullet');
                paginationEl?.append(bullet);
            },
            reachEnd: () => {
                nextButton.addEventListener('click', lastSlideEvent);
            },
            slideChange: (element: Swiper) => {
                if (element.previousIndex === element.slides.length - 1) {
                    nextButton.removeEventListener('click', lastSlideEvent);
                }
            }
        }

    });

    swiper.init();

    const nextButton = swiper.navigation.nextEl;

    const lastSlideEvent = (): void => {
        initPlanPopup(sliderWithPopup);
    };
};

/**
 * Инициализация слайдера
 * @param {HTMLElement} sliderWrap - контейнер слайдера
 */
export const initImageSlider = (sliderWrap: HTMLElement): void => {
    const paginationEl = sliderWrap.querySelector<HTMLElement>('.j-slider-counter');
    const slidesLength = sliderWrap.querySelectorAll('.swiper-slide').length;
    const sliderWithPopup = sliderWrap.dataset['sliderPopup'];

    sliderWithPopup ?
        initImageSliderPopup(sliderWrap, slidesLength, paginationEl, sliderWithPopup) :
        initImageSliderDefault(sliderWrap, slidesLength, paginationEl);
};

/* Подключение ленивой загрузки (img/picture/iframe) */
export const lazyLoadElements = (lazyElements: Element[]): void => {
    const replaceSource = (element: Element): void => {
        const parentElement = element.parentElement;

        if (!parentElement) {
            return;
        }

        if (parentElement.tagName === 'PICTURE' && element.tagName === 'IMG') {
            element.addEventListener('load', () => {
                parentElement.style.backgroundImage = '';
            });
        }

        const targetAttribute = element.nodeName === 'SOURCE' ? 'srcset' : 'src';
        const sourceElement = element.nodeName === 'SOURCE' ? 'data-srcset' : 'data-src';

        if (element.getAttribute(sourceElement) !== null) {
            element.setAttribute(targetAttribute, element.getAttribute(sourceElement) || '');
            element.removeAttribute(sourceElement);
        }
    };

    const replaceSourcePicture = (element: HTMLElement): void => {
        const lazyElementsPicture = [...element.querySelectorAll<HTMLElement>('[loading=lazy]')];

        lazyElementsPicture.forEach((lazyEl: Element) => {
            replaceSource(lazyEl);
        });
    };

    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const lazyImageObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    const parent = lazyEl.parentElement;

                    parent?.tagName === 'PICTURE' ?
                        replaceSourcePicture(parent) :
                        replaceSource(lazyEl);

                    lazyImageObserver.unobserve(lazyEl);
                }
            });
        });

        lazyElements.forEach((lazyImage: Element) => {
            if (Utils.isInViewport(lazyImage as HTMLElement,
                window.innerHeight || document.documentElement.clientHeight)) {
                replaceSource(lazyImage);
            } else {
                lazyImageObserver.observe(lazyImage);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyElements.forEach((lazyEl: Element) => {
            replaceSource(lazyEl);
        });
    }
};

export const lazyLoadCounters = (lazyCounters: HTMLElement[]): void => {
    const startCounter = (element: HTMLElement): void => {
        const counter = new Counter(element);

        counter.init();
    };

    if ('IntersectionObserver' in window) {
        // Иначе по событию IntersectionObserver
        const lazyCounterObserver = new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry: IntersectionObserverEntry) => {
                if (entry.isIntersecting) {
                    const lazyEl = entry.target;

                    startCounter(lazyEl as HTMLElement);
                }
            });
        });

        lazyCounters.forEach((lazyCounter: HTMLElement) => {
            if (Utils.isInViewport(lazyCounter,
                window.innerHeight || document.documentElement.clientHeight)) {
                startCounter(lazyCounter);
            } else {
                lazyCounterObserver.observe(lazyCounter);
            }
        });
    } else {
        // Если есть нативная поддержка атрибута или нет поддержки IntersectionObserver - подменяем сразу
        lazyCounters.forEach((lazyEl: Element) => {
            startCounter(lazyEl as HTMLElement);
        });
    }
};

export const initPlanoplanWidget = (uid: string): void => {
    const primaryColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-brand-base') || null;

    window.Planoplan.init({
        uid,
        width          : '100%',
        height         : '100%',
        el             : 'planoplan',
        textColor      : '#000000',
        backgroundColor: '#ffffff',
        ...primaryColor && {
            primaryColor
        }
    });
};

export const insertPremisesData = (wrapper: HTMLElement, data: ClassTypes.IPopupIframeData): void => {
    const currentForm = wrapper.querySelector('.j-form') as HTMLElement;
    const container = currentForm?.querySelector('.j-form__parametric-content') as HTMLElement;

    if (!currentForm || !container) {
        return;
    }

    const fillField = currentForm.querySelector('.j-form__fill-field') as HTMLInputElement;

    if (fillField) {
        fillField.value = `${data.description}`;
    }

    Utils.clearHtml(container);
    Utils.insetContent(container, popupContentTemplate(data));
};

export const initVideoTabs = (wrapperSelector: string): void => {
    const wrapper = document.querySelector(wrapperSelector);

    if (wrapper) {
        const tabs: HTMLElement[] = Array.from(wrapper.querySelectorAll('.j-video__tab'));
        const iframe = wrapper.querySelector('.j-video__iframe') as HTMLIFrameElement;
        const isActive = 'is-active';

        tabs.forEach((tab: HTMLElement): void => {
            tab.addEventListener('click', (event: MouseEvent) => {
                const target = event.currentTarget as HTMLElement;

                if (!target || !iframe) {
                    return;
                }

                iframe.src = target.dataset['video'] || '';
                tabs.forEach((element: HTMLElement) => {
                    const method = element === target ? 'add' : 'remove';

                    element.classList[method](isActive);
                });
            });
        });
    }
};

export const initPopup = (buttons: HTMLElement[], otherOptions: Partial<ClassTypes.IPopupFacadeOptions> = {}): void => {
    import(/* webpackChunkName: "popup-facade" */ '@/components/popup/popupFacade')
        .then(({default: PopupFacade}: IDynamicImport) => {
            const options: ClassTypes.IPopupFacadeOptions = {
                ...{openButtons: buttons},
                ...otherOptions
            };

            const t: ClassTypes.IPopupFacade = new PopupFacade(options);

            t.init();
        });
};

// Инициализация форм
export const initForm = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "form" */ '@/components/form')
        .then(({default: Form}: IDynamicImport) => {
            new Form().init({
                target        : wrapper,
                successMessage: true
            });
        });
};

// Инициализация формы обратной связи в попапах
export const initCallbackForm = (): void => {
    observer.subscribe('popup:open', (popup: ClassTypes.IPopup) => {
        if (!popup.content) {
            return;
        }

        // Форма
        const callbackFormElement = popup.content.querySelector('.j-form') as HTMLElement;

        if (callbackFormElement) {
            validation.update();
            import(/* webpackChunkName: "form" */ '@/components/form')
                .then(({default: Form}: IDynamicImport) => {
                    new Form().init({
                        target        : callbackFormElement,
                        popupInstance : popup,
                        successMessage: true
                    });
                });
        }
    });
};

// Инициализация попапов с формой обратной связи
export const initCallbackPopup = (buttons: HTMLElement[], modify: string = 'popup_theme_callback'): void => {
    const options = {
        removeDelay         : 300,
        closeButtonAriaLabel: 'Закрыть форму обратного звонка',
        modify
    };

    initCallbackForm();
    initPopup(buttons, options);
};

export const initImagePoints = (wrapper: HTMLElement): void => {
    import(/* webpackChunkName: "image-point" */ '@/components/image-point')
        .then(({default: ImagePoints}: IDynamicImport) => {
            new ImagePoints(wrapper).init();
        });
};

// Табы
export const initTabs = (elem: HTMLElement): void => {
    import(/* webpackChunkName: "tabs" */ '@/components/tabs')
        .then(({default: Tabs}: IDynamicImport) => {
            new Tabs().init({elem});
        });
};

/**
 * Инициализация попапа в последнем слайде слайдера
 * @param {string} popupID - идентификатор попапа
 */
const initPlanPopup = (popupID: string): void => {
    const popupButtonArray: HTMLElement[] = [];
    const popupButton: HTMLElement = document.createElement('div');

    popupButton.dataset['callback'] = 'true';
    popupButton.dataset['href'] = popupID;
    popupButton.dataset['init'] = 'true';

    window.history.pushState(null, '', `#${popupID}`);

    popupButtonArray.push(popupButton);

    initCallbackPopup(popupButtonArray, 'popup_theme_plans');
};
